import { createStore } from "vuex";
import { authService } from "@/services/auth.service";
import { customerService } from "@/services/customer.service";
import { honoraryService } from "@/services/honorary.service";
import { userService } from "@/services/user.service";
import type {
  AuthUser,
  BackendUser,
  Customer,
  CustomerFilters,
  Honorary,
  HonoraryFilters,
  LoginCredentials
} from "@/types";
import { STORAGE_KEYS } from "@/config/api";

/** Mescla GET /users/:id no usuário logado (`photoUrl` = URL pública R2 quando o backend envia). */
function mergeProfileIntoAuthUser(existing: AuthUser | null, profile: BackendUser): AuthUser {
  const displayName = `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
  return {
    email: profile.email || existing?.email || "",
    userId: existing?.userId || profile.id,
    username: profile.username ?? existing?.username,
    displayName: displayName || existing?.displayName,
    phone: existing?.phone,
    companyName: existing?.companyName,
    role: existing?.role,
    notes: existing?.notes,
    photoUrl: profile.photoUrl ?? existing?.photoUrl ?? null,
    avatar: existing?.avatar,
    cpf: profile.cpf || existing?.cpf,
    bornDate: profile.bornDate || existing?.bornDate,
    street: profile.street ?? existing?.street,
    state: profile.state ?? existing?.state,
    country: profile.country ?? existing?.country,
    neighborhood: profile.neighborhood ?? existing?.neighborhood
  };
}

function getStoredUser(): AuthUser | null {
  const savedUser = localStorage.getItem(STORAGE_KEYS.USER);

  if (!savedUser) {
    return null;
  }

  try {
    return JSON.parse(savedUser) as AuthUser;
  } catch {
    return null;
  }
}

interface State {
  isDarkTheme: boolean;
  isAuthenticated: boolean;
  currentUser: AuthUser | null;
  clientes: Customer[];
  honorarios: Honorary[];
  loading: boolean;
  error: string | null;
}

export default createStore<State>({
  state: {
    isDarkTheme: true,
    isAuthenticated: authService.isAuthenticated(),
    currentUser: getStoredUser(),
    clientes: [],
    honorarios: [],
    loading: false,
    error: null
  },
  mutations: {
    TOGGLE_THEME(state) {
      state.isDarkTheme = !state.isDarkTheme;
    },
    SET_AUTH(state, { isAuthenticated, user }) {
      state.isAuthenticated = isAuthenticated;
      state.currentUser = user;
    },
    SET_CURRENT_USER(state, user: AuthUser | null) {
      state.currentUser = user;
    },
    SET_CLIENTES(state, clientes: Customer[]) {
      state.clientes = clientes;
    },
    ADD_CLIENTE(state, cliente: Customer) {
      state.clientes.push(cliente);
    },
    UPDATE_CLIENTE(state, cliente: Customer) {
      const index = state.clientes.findIndex((c) => c.id === cliente.id);
      if (index !== -1) {
        state.clientes[index] = cliente;
      }
    },
    REMOVE_CLIENTE(state, id: string) {
      state.clientes = state.clientes.filter((c) => c.id !== id);
    },
    SET_HONORARIOS(state, honorarios: Honorary[]) {
      state.honorarios = honorarios;
    },
    ADD_HONORARIO(state, honorario: Honorary) {
      state.honorarios.push(honorario);
    },
    UPDATE_HONORARIO(state, honorario: Honorary) {
      const index = state.honorarios.findIndex((h) => h.id === honorario.id);
      if (index !== -1) {
        state.honorarios[index] = honorario;
      }
    },
    REMOVE_HONORARIO(state, id: string) {
      state.honorarios = state.honorarios.filter((h) => h.id !== id);
    },
    SET_LOADING(state, loading: boolean) {
      state.loading = loading;
    },
    SET_ERROR(state, error: string | null) {
      state.error = error;
    }
  },
  actions: {
    async login({ commit }, credentials: LoginCredentials) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        
        const response = await authService.login(credentials);
        
        // Decodificar token JWT para obter informações do usuário (simplificado)
        // Em produção, use uma biblioteca como jwt-decode
        const tokenParts = response.authToken.split('.');
        let userData: AuthUser | null = null;
        if (tokenParts.length === 3) {
          try {
            const payload = JSON.parse(atob(tokenParts[1])) as Record<string, unknown>;
            const uid =
              (typeof payload.userId === 'string' && payload.userId) ||
              (typeof payload.sub === 'string' && payload.sub) ||
              (typeof payload.id === 'string' && payload.id) ||
              undefined;
            userData = {
              email: (typeof payload.email === 'string' && payload.email) || credentials.email,
              userId: uid
            };
          } catch (e) {
            // Se não conseguir decodificar, usar email do login
            userData = { email: credentials.email };
          }
        }

        if (userData?.userId) {
          try {
            const profile = await userService.getUserById(userData.userId);
            userData = mergeProfileIntoAuthUser(userData, profile);
          } catch {
            /* mantém usuário mínimo do JWT */
          }
        }

        commit("SET_AUTH", {
          isAuthenticated: true,
          user: userData
        });

        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        
        return { success: true };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao fazer login");
        return { success: false, error: error.message || "Erro ao fazer login" };
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async logout({ commit }) {
      authService.logout();
      commit("SET_AUTH", { isAuthenticated: false, user: null });
      commit("SET_CLIENTES", []);
      commit("SET_HONORARIOS", []);
    },

    updateCurrentUser({ commit }, user: AuthUser) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      commit("SET_CURRENT_USER", user);
      return { success: true, user };
    },

    /**
     * Preenche `photoUrl` e demais campos do perfil quando o login só gravou JWT mínimo,
     * ou quando o localStorage antigo não tinha foto.
     */
    async hydrateCurrentUser({ state, dispatch }) {
      if (!state.isAuthenticated || !state.currentUser?.userId) {
        return;
      }
      try {
        const profile = await userService.getUserById(state.currentUser.userId);
        const merged = mergeProfileIntoAuthUser(state.currentUser, profile);
        await dispatch("updateCurrentUser", merged);
      } catch {
        /* offline / token inválido — ignorar */
      }
    },
    
    async loadClientes({ commit }, filters: CustomerFilters = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const clientes = await customerService.listCustomers(filters);
        commit("SET_CLIENTES", clientes);
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao carregar clientes");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async cadastrarCliente({ commit }, clienteData) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const cliente = await customerService.createCustomer(clienteData);
        commit("ADD_CLIENTE", cliente);
        return { success: true, cliente };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao cadastrar cliente");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async atualizarCliente({ commit }, { id, data }) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const cliente = await customerService.updateCustomer(id, data);
        commit("UPDATE_CLIENTE", cliente);
        return { success: true, cliente };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao atualizar cliente");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async deletarCliente({ commit }, id: string) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        await customerService.deleteCustomer(id);
        commit("REMOVE_CLIENTE", id);
        return { success: true };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao deletar cliente");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async loadHonorarios({ commit }, filters: HonoraryFilters = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const honorarios = await honoraryService.listHonoraries(filters);
        commit("SET_HONORARIOS", honorarios);
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao carregar honorários");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async cadastrarHonorario({ commit }, honorarioData) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const honorario = await honoraryService.createHonorary(honorarioData);
        commit("ADD_HONORARIO", honorario);
        return { success: true, honorario };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao cadastrar honorário");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async atualizarStatusHonorario({ commit }, { id, status }) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const honorario = await honoraryService.updateStatus(id, status);
        commit("UPDATE_HONORARIO", honorario);
        return { success: true, honorario };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao atualizar status");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async atualizarHonorario({ commit }, { id, data }) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const honorario = await honoraryService.updateHonorary(id, data);
        commit("UPDATE_HONORARIO", honorario);
        return { success: true, honorario };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao atualizar honorário");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async deletarHonorario({ commit }, id: string) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        await honoraryService.deleteHonorary(id);
        commit("REMOVE_HONORARIO", id);
        return { success: true };
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao deletar honorário");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    
    async getHonorarySummary({ commit }, filters = {}) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        const summary = await honoraryService.getSummary(filters);
        return summary;
      } catch (error: any) {
        commit("SET_ERROR", error.message || "Erro ao carregar resumo");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    }
  },
  getters: {
    honorariosPendentes: (state) => 
      state.honorarios.filter((h) => h.status === "Pendente"),
    honorariosPagos: (state) => 
      state.honorarios.filter((h) => h.status === "Recebido"),
    totalPendente: (state) =>
      state.honorarios
        .filter((h) => h.status === "Pendente")
        .reduce((total, h) => total + (h.value - h.paidValue), 0),
    totalRecebido: (state) =>
      state.honorarios
        .filter((h) => h.status === "Recebido")
        .reduce((total, h) => total + h.paidValue, 0)
  }
});
